import { useAtomValue, useSetAtom } from 'jotai';
import { ComponentProps, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { MeasureConnected, MeasureResponseCompletionCampaign } from '@grow-therapy-team/seedling-components';

import { PageComponentProps, PaginatedDrawer } from '../../components';
import { PendingFormsDrawerHeader } from '../../components/forms/PendingFormsDrawerHeader';
import { GroupedFormData } from '../../components/forms/types';
import { MeasuresActionsWrapper as BaseMeasuresActions } from '../../components/measures/MeasuresActions';
import { MeasuresContent as BaseMeasuresContent } from '../../components/measures/MeasuresContent';
import { MeasuresDrawer, MeasuresDrawerLoadingSkeleton } from '../../components/measures/MeasuresDrawer';
import { MeasureSchemaAndAnswers } from '../../components/measures/types';
import {
  VisitorState,
  hasCompletedClientMeasuresAtom,
  visitorGroupedFormsAtom,
  visitorMeasuresAtom,
  visitorMeasuresPageIndexAtom,
  visitorStateAtom,
} from '../state';
import { useRecordClientMeasure } from '../useRecordClientMeasure';
import { deadlineAtom } from '../waiting-room/delayed-entry/state';
import { usePreSessionPromptState } from '../waiting-room/pre-session-prompt/usePreSessionPromptState';
import { ClientPendingFormsDrawerActions } from './ClientPendingFormsDrawerAction';
import { ClientPendingFormsWrapper as PendingForm } from './ClientPendingFormsWrapper';
import { AppointmentStatus, YoureAllSetForYourAppointmentCard } from './YoureAllSetForYourAppointmentCard';
import { useAutoCloseMeasuresDrawer } from './useAutoCloseMeasuresDrawer';
import { useGetClientUserShortId } from './useGetClientUserShortId';
import { useTrackMeasureStarted } from './useTrackMeasureStarted';

function Measure(props: ComponentProps<typeof MeasureConnected>) {
  const onValuesChange = useTrackMeasureStarted();
  return <MeasureConnected measureHeadingAs='h3' {...props} onValuesChangeCallback={onValuesChange} />;
}

function MeasuresContent(props: ComponentProps<typeof BaseMeasuresContent>) {
  return <BaseMeasuresContent measureComponent={Measure} {...props} />;
}

export function ClientMeasuresDrawer() {
  const visitorForms = useAtomValue(visitorGroupedFormsAtom);

  if (!visitorForms) return undefined;

  return (
    <>
      <PaginatedDrawer<GroupedFormData>
        contentComponent={PendingForm}
        contentOnly
        drawerBodyProps={{ className: 'bg-neutral-200' }}
        headerComponent={PendingFormsDrawerHeader}
        headingAs='h2'
        pages={visitorForms}
        actionsComponent={ClientPendingFormsDrawerActions}
        drawerBodyKeyFn={({ page }) => `pending-forms-drawer.${page?.formShortId}`}
      />
    </>
  );
}

function MeasuresActionsWrapper(props: PageComponentProps<MeasureSchemaAndAnswers>) {
  const clientUserShortId = useGetClientUserShortId();
  const { providerShortId } = useParams();
  const { clearPreSessionPrompt } = usePreSessionPromptState();
  const setMeasures = useSetAtom(visitorMeasuresAtom);
  const setHasCompletedClientMeasures = useSetAtom(hasCompletedClientMeasuresAtom);
  const setMeasuresPageIndex = useSetAtom(visitorMeasuresPageIndexAtom);
  const [recordMeasure] = useRecordClientMeasure(providerShortId);
  const handleSuccessfulSubmission = () => {
    if (providerShortId) {
      recordMeasure();
    }
    if (props.isLastPage) {
      setMeasures([]);
      setHasCompletedClientMeasures(true);
      setMeasuresPageIndex(0);
      clearPreSessionPrompt();
    }
  };

  return (
    <BaseMeasuresActions
      {...props}
      clientUserShortId={clientUserShortId}
      providerShortId={providerShortId}
      completionCampaign={MeasureResponseCompletionCampaign.WAITING_ROOM}
      onSubmitMeasureSuccess={handleSuccessfulSubmission}
    />
  );
}

export function ClientMeasuresDrawerWrapper() {
  const measures = useAtomValue(visitorMeasuresAtom);
  const pages = measures?.map((schema) => ({
    schema: schema.schema,
    hasSubmitted: false,
    answers: {},
    errors: [],
    provider: schema.providers?.[0] ?? undefined,
  }));
  const visitorState = useAtomValue(visitorStateAtom);
  const deadline = useAtomValue(deadlineAtom);
  const savedPageIndex = useAtomValue(visitorMeasuresPageIndexAtom);
  const setMeasuresPageIndex = useSetAtom(visitorMeasuresPageIndexAtom);

  useAutoCloseMeasuresDrawer();

  const handlePageIndexChange = useCallback(
    (pageIndex: number) => setMeasuresPageIndex(pageIndex),
    [setMeasuresPageIndex],
  );

  const status: AppointmentStatus =
    visitorState === VisitorState.MEETING ? 'in-session' : deadline ? 'provider-ready' : 'waiting';

  // Pages is initially undefined before measures finishes loading
  if (!pages) {
    return <MeasuresDrawerLoadingSkeleton />;
  }

  // Once loaded, if there are no pages, there are no outstanding measures
  if (!pages.length) {
    return <YoureAllSetForYourAppointmentCard status={status} />;
  }

  const initialPageIndex = Math.min(savedPageIndex, pages.length - 1);

  return (
    <MeasuresDrawer
      pages={pages}
      headingAs='h3'
      initialPageIndex={initialPageIndex}
      contentComponent={MeasuresContent}
      actionsComponent={MeasuresActionsWrapper}
      drawerBodyProps={{ className: '!py-0' }}
      onPageIndexChange={handlePageIndexChange}
    />
  );
}
